import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.modal = this.element
    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.handleKeydown)

    // Listen for programmatic open events
    this.handleOpen = this.handleOpen.bind(this)
    window.addEventListener("auth-modal:open", this.handleOpen)

    // Listen for data-auth-open clicks
    this.handleAuthClick = this.handleAuthClick.bind(this)
    document.addEventListener("click", this.handleAuthClick)

    // Auto-open from URL params (initial load + Turbo navigations)
    this.handleTurboLoad = this.checkUrlParams.bind(this)
    document.addEventListener("turbo:load", this.handleTurboLoad)
    this.checkUrlParams()
  }

  checkUrlParams() {
    const params = new URLSearchParams(window.location.search)
    const authParam = params.get("auth")
    if (authParam === "login" || authParam === "signup") {
      this.open(authParam)
      // Clean URL without reload
      const url = new URL(window.location)
      url.searchParams.delete("auth")
      window.history.replaceState({}, "", url)
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    window.removeEventListener("auth-modal:open", this.handleOpen)
    document.removeEventListener("click", this.handleAuthClick)
    document.removeEventListener("turbo:load", this.handleTurboLoad)
  }

  handleAuthClick(e) {
    const trigger = e.target.closest("[data-auth-open]")
    if (!trigger) return
    e.preventDefault()
    const mode = trigger.dataset.authOpen || "login"
    const intended = trigger.dataset.authIntended || ""
    this.open(mode, intended)
  }

  handleOpen(e) {
    const { mode, intended } = e.detail || {}
    this.open(mode || "login", intended || "")
  }

  open(mode = "login", intended = "") {
    this.modal.classList.remove("hidden")
    document.body.style.overflow = "hidden"

    // Set return_to
    const loginReturn = this.modal.querySelector("#auth-return-to-login")
    const signupReturn = this.modal.querySelector("#auth-return-to-signup")
    if (loginReturn) loginReturn.value = intended
    if (signupReturn) signupReturn.value = intended

    if (mode === "signup") {
      this.showSignup()
    } else {
      this.showLogin()
    }

    this.clearError()
  }

  close() {
    this.modal.classList.add("hidden")
    document.body.style.overflow = ""
    this.clearError()
    this.resetForms()
  }

  showLogin() {
    this.modal.querySelector("#auth-form-login").classList.remove("hidden")
    this.modal.querySelector("#auth-form-signup").classList.add("hidden")
    this.modal.querySelector("#auth-tab-login").classList.add("bg-white", "text-slate-900", "shadow-sm")
    this.modal.querySelector("#auth-tab-login").classList.remove("text-slate-500", "hover:text-slate-700")
    this.modal.querySelector("#auth-tab-signup").classList.remove("bg-white", "text-slate-900", "shadow-sm")
    this.modal.querySelector("#auth-tab-signup").classList.add("text-slate-500", "hover:text-slate-700")
    this.modal.querySelector("#auth-modal-title").textContent = "Welcome back"
    this.modal.querySelector("#auth-modal-subtitle").textContent = "Sign in to your account"
    this.clearError()
  }

  showSignup() {
    this.modal.querySelector("#auth-form-signup").classList.remove("hidden")
    this.modal.querySelector("#auth-form-login").classList.add("hidden")
    this.modal.querySelector("#auth-tab-signup").classList.add("bg-white", "text-slate-900", "shadow-sm")
    this.modal.querySelector("#auth-tab-signup").classList.remove("text-slate-500", "hover:text-slate-700")
    this.modal.querySelector("#auth-tab-login").classList.remove("bg-white", "text-slate-900", "shadow-sm")
    this.modal.querySelector("#auth-tab-login").classList.add("text-slate-500", "hover:text-slate-700")
    this.modal.querySelector("#auth-modal-title").textContent = "Create your account"
    this.modal.querySelector("#auth-modal-subtitle").textContent = "Start building interactive explanations"
    this.clearError()
  }

  async submitLogin(e) {
    e.preventDefault()
    const form = e.target
    const formData = new FormData(form)
    const submitBtn = this.modal.querySelector("#auth-submit-login")
    const spinner = this.modal.querySelector("#auth-spinner-login")

    this.setLoading(submitBtn, spinner, true)
    this.clearError()

    try {
      const res = await fetch("/session.json", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({
          login: formData.get("login"),
          password: formData.get("password"),
          return_to: formData.get("return_to"),
        }),
      })

      const data = await res.json()

      if (data.success) {
        this.updateCsrfToken(data.csrf_token)
        window.dispatchEvent(new CustomEvent("auth-modal:success", { detail: data }))
        window.location.href = data.redirect_to || "/"
      } else {
        this.showError(data.error)
      }
    } catch {
      this.showError("Something went wrong. Please try again.")
    } finally {
      this.setLoading(submitBtn, spinner, false)
    }
  }

  async submitSignup(e) {
    e.preventDefault()
    const form = e.target
    const formData = new FormData(form)
    const submitBtn = this.modal.querySelector("#auth-submit-signup")
    const spinner = this.modal.querySelector("#auth-spinner-signup")

    this.setLoading(submitBtn, spinner, true)
    this.clearError()

    try {
      const res = await fetch("/registration.json", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({
          username: formData.get("username"),
          email_address: formData.get("email_address"),
          password: formData.get("password"),
          password_confirmation: formData.get("password_confirmation"),
          return_to: formData.get("return_to"),
        }),
      })

      const data = await res.json()

      if (data.success) {
        this.updateCsrfToken(data.csrf_token)
        window.dispatchEvent(new CustomEvent("auth-modal:success", { detail: data }))
        window.location.href = data.redirect_to || "/"
      } else {
        this.showError(data.error)
      }
    } catch {
      this.showError("Something went wrong. Please try again.")
    } finally {
      this.setLoading(submitBtn, spinner, false)
    }
  }

  handleKeydown(e) {
    if (e.key === "Escape" && !this.modal.classList.contains("hidden")) {
      this.close()
    }
  }

  showError(message) {
    const errorEl = this.modal.querySelector("#auth-modal-error")
    errorEl.textContent = message
    errorEl.classList.remove("hidden")
  }

  clearError() {
    const errorEl = this.modal.querySelector("#auth-modal-error")
    errorEl.textContent = ""
    errorEl.classList.add("hidden")
  }

  resetForms() {
    this.modal.querySelectorAll("form").forEach((f) => f.reset())
  }

  setLoading(btn, spinner, loading) {
    if (loading) {
      btn.disabled = true
      btn.classList.add("opacity-70")
      spinner.style.display = ""
      spinner.classList.add("animate-spin")
    } else {
      btn.disabled = false
      btn.classList.remove("opacity-70")
      spinner.style.display = "none"
      spinner.classList.remove("animate-spin")
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  updateCsrfToken(token) {
    if (!token) return
    const meta = document.querySelector('meta[name="csrf-token"]')
    if (meta) meta.content = token
  }
}
